import type { Category, Note, NoteStatus } from "@ccnote/shared";

export const API_URL: string = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Fetch against the API with the session cookie. */
export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  const res = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: "include" });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? res.statusText);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export const json = (body: unknown) => JSON.stringify(body);

export function googleSignInUrl(redirectPath = "/"): string {
  const redirect = `${window.location.origin}${redirectPath}`;
  return `${API_URL}/auth/google/start?redirect=${encodeURIComponent(redirect)}`;
}

export function attachmentUrl(noteId: string, attachmentId: string, inline = false): string {
  return `${API_URL}/v1/notes/${noteId}/attachments/${attachmentId}${inline ? "?inline=1" : ""}`;
}

export interface SessionUser {
  username: string;
  displayName: string | null;
  email: string;
  role: "user" | "admin";
  address: string;
}

export interface NotesPage {
  notes: Note[];
  nextCursor: string | null;
}

export interface NotesFilter {
  status: NoteStatus;
  category?: string;
  q?: string;
}

export interface Attachment {
  id: string;
  filename: string | null;
  mime: string | null;
  size: number;
  /** Signed, short-lived URL on media.ccnote.me (null if the server has no media host configured). */
  url: string | null;
}

/** A private note about an X account, added from the browser extension; kept by the account's numeric id. */
export interface XRemark {
  xUserId: string;
  handle: string;
  name: string | null;
  remark: string;
  /** Earlier handles/names, newest first. */
  pastHandles: { handle: string; name: string | null; until: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface Sender {
  address: string;
  verifiedAt: string | null;
  blocked: boolean;
}

export interface ApiToken {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export type { Category, Note };

export interface StorageUsage {
  plan: string;
  limit: number;
  quota: { base: number; purchased: number; custom: boolean };
  packs: PackPeriod[];
  used: number;
  breakdown: { text: number; pictures: number; videos: number; files: number; emails: number };
  trashedNotes: number;
}

export interface StoredFile {
  id: string;
  filename: string | null;
  mime: string | null;
  size: number;
  noteId: string;
  noteTitle: string;
  noteStatus: string;
  createdAt: string;
  url: string | null;
}

export interface FilesPage {
  files: StoredFile[];
  total: { count: number; bytes: number };
  nextOffset: number | null;
}

export interface StoragePack {
  id: string;
  label: string;
  bytes: number;
  amount: number;
  currency: string;
  days: number;
}

/** A bought pack and until when it counts (renewals extend it). */
export interface PackPeriod {
  orderId: string;
  packId: string | null;
  bytes: number;
  paidAt: string;
  expiresAt: string;
  active: boolean;
}

export interface StorageOrder {
  id: string;
  packId: string | null;
  bytes: number;
  amount: number;
  currency: string;
  status: "pending" | "paid" | "expired" | "refunded" | "failed";
  payUrl: string | null;
  method: string | null;
  paidAt: string | null;
  createdAt: string;
  days: number;
  renewsOrderId: string | null;
}

export interface Billing {
  available: boolean;
  packs: StoragePack[];
  orders: StorageOrder[];
  periods: PackPeriod[];
}
